import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Leaf,
  Phone,
  ShieldCheck,
  Sprout,
  Truck,
} from "lucide-react";

import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";

import LettuceImage from "../image/lettuce_2.png";
import SpinachImage from "../image/spinach.png";
import BasilImage from "../image/basil.png";
import MintImage from "../image/mint.png";
import TomatoImage from "../image/tomato.png";
import CucumberImage from "../image/cucumber2.png";
import StrawberryImage from "../image/strawberry.png";
import CapsicumImage from "../image/capsicum.png";
import CorianderImage from "../image/coriander.png";
import MicrogreensImage from "../image/microgreens.png";

const products = [
  {
    slug: "lettuce",
    name: "Lettuce",
    category: "Leafy Greens",
    description: "Crisp, fresh and nutritious",
    image: LettuceImage,
    features: [
      "Rich in vitamins A & K",
      "Low calories, high fiber",
      "Perfect for salads",
    ],
  },
  {
    slug: "spinach",
    name: "Spinach",
    category: "Leafy Greens",
    description: "Tender leaves, rich in iron",
    image: SpinachImage,
    features: [
      "High in iron & antioxidants",
      "Supports overall health",
      "Available year-round",
    ],
  },
  {
    slug: "basil",
    name: "Basil",
    category: "Herbs",
    description: "Aromatic and flavourful",
    image: BasilImage,
    features: [
      "Rich aroma and taste",
      "Ideal for culinary use",
      "Fresh and chemical-free",
    ],
  },
  {
    slug: "mint",
    name: "Mint",
    category: "Herbs",
    description: "Refreshing and natural",
    image: MintImage,
    features: [
      "Cooling and fresh flavour",
      "Great for beverages",
      "High-quality leaves",
    ],
  },
  {
    slug: "tomato-cherry",
    name: "Tomato (Cherry)",
    category: "Fruits",
    description: "Sweet, juicy and fresh",
    image: TomatoImage,
    features: [
      "Rich in vitamins C & K",
      "Naturally sweet taste",
      "Grown in controlled environment",
    ],
  },
  {
    slug: "cucumber",
    name: "Cucumber",
    category: "Vegetables",
    description: "Crisp and hydrating",
    image: CucumberImage,
    features: [
      "High water content",
      "Fresh and crunchy",
      "Ideal for healthy diet",
    ],
  },
  {
    slug: "strawberry",
    name: "Strawberry",
    category: "Fruits",
    description: "Sweet and delicious",
    image: StrawberryImage,
    features: [
      "Naturally sweet taste",
      "Rich in antioxidants",
      "Premium quality produce",
    ],
  },
  {
    slug: "capsicum",
    name: "Capsicum (Bell Pepper)",
    category: "Vegetables",
    description: "Colourful and nutritious",
    image: CapsicumImage,
    features: [
      "Rich in vitamins A & C",
      "Crisp and flavourful",
      "Available year-round",
    ],
  },
  {
    slug: "coriander",
    name: "Coriander",
    category: "Herbs",
    description: "Fresh and aromatic",
    image: CorianderImage,
    features: [
      "Strong natural aroma",
      "Used in daily cooking",
      "Clean and chemical-free",
    ],
  },
  {
    slug: "microgreens",
    name: "Microgreens",
    category: "Microgreens",
    description: "Small leaves, big nutrition",
    image: MicrogreensImage,
    features: [
      "Nutrient-dense superfood",
      "Perfect for salads & garnishes",
      "Quick growth cycle",
    ],
  },
];

export default function ProductDetails() {
  const { slug } = useParams();

  const product = products.find(
    (item) => item.slug === slug
  );

  if (!product) {
    return (
      <>

        <section className="flex min-h-[60vh] items-center justify-center px-5">
          <div className="text-center">

            <h1 className="text-4xl font-extrabold text-[#10231c]">
              Product Not Found
            </h1>

            <p className="mt-3 text-gray-500">
              Sorry, this product could not be found.
            </p>

            <Link
              to="/products"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-sm font-bold text-white"
            >
              <ArrowLeft size={16} />
              Back to Products
            </Link>

          </div>
        </section>

      </>
    );
  }

  const relatedProducts = products
    .filter((item) => item.slug !== product.slug)
    .slice(0, 4);

  return (
    <>
      <main className="bg-white text-[#10231c]">

        {/* ================================================
            BREADCRUMB
        ================================================= */}

        <section className="border-b border-green-100 bg-[#f7fbf8]">
          <div className="mx-auto max-w-[1400px] px-5 py-4 lg:px-10">

            <div className="flex items-center gap-2 text-sm text-gray-500">

              <Link
                to="/"
                className="hover:text-[#087f4f]"
              >
                Home
              </Link>

              <span>›</span>

              <Link
                to="/products"
                className="hover:text-[#087f4f]"
              >
                Products
              </Link>

              <span>›</span>

              <span className="font-medium text-[#087f4f]">
                {product.name}
              </span>

            </div>

          </div>
        </section>


        {/* ================================================
            PRODUCT HERO
        ================================================= */}

        <section className="py-12 lg:py-16">

          <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

            <div className="grid items-center gap-12 lg:grid-cols-2">

              {/* IMAGE */}

              <div className="relative overflow-hidden rounded-3xl bg-[#f4faf6]">

                <img
                  src={product.image}
                  alt={product.name}
                  className="h-[400px] w-full object-cover sm:h-[520px]"
                />

                <div className="absolute left-5 top-5 rounded-full bg-[#087f4f] px-4 py-2 text-xs font-bold text-white">
                  Fresh Produce
                </div>

              </div>


              {/* CONTENT */}

              <div>

                <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                  {product.category}
                </p>

                <h1 className="mt-2 text-4xl font-extrabold leading-tight sm:text-5xl">
                  {product.name}
                </h1>

                <p className="mt-4 text-lg text-gray-600">
                  {product.description}
                </p>


                {/* Badge */}

                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 text-sm font-bold text-[#087f4f]">
                  <Leaf size={16} />
                  Fresh • Local • Sustainable
                </div>


                <p className="mt-6 leading-7 text-gray-600">
                  Our {product.name} is carefully grown using
                  modern hydroponic farming techniques in a
                  controlled environment for consistent quality,
                  freshness and nutrition.
                </p>


                {/* FEATURES */}

                <div className="mt-7 space-y-3">

                  {product.features.map(
                    (feature, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3"
                      >

                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
                          <Check size={14} />
                        </span>

                        <span className="text-sm font-medium text-gray-700">
                          {feature}
                        </span>

                      </div>
                    )
                  )}

                </div>


                {/* BUTTONS */}

                <div className="mt-8 flex flex-wrap gap-3">

                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-7 py-3.5 text-sm font-bold text-white transition hover:bg-[#066b42]"
                  >
                    Get a Quote
                    <ArrowRight size={17} />
                  </Link>

                  <a
                    href="tel:+919876543210"
                    className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-7 py-3.5 text-sm font-bold text-gray-700 transition hover:border-[#087f4f] hover:text-[#087f4f]"
                  >
                    <Phone size={17} />
                    Talk to Our Experts
                  </a>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* ================================================
            BENEFITS
        ================================================= */}

        <section className="bg-[#f7fbf8] py-14">

          <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

            <div className="mb-9">

              <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                Why Choose Our Produce
              </p>

              <h2 className="mt-2 text-3xl font-extrabold">
                Freshness You Can Trust
              </h2>

            </div>


            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">

              {[
                {
                  icon: <Leaf size={25} />,
                  title: "Fresh & Natural",
                  text: "Freshly grown produce with careful cultivation.",
                },
                {
                  icon: <Sprout size={25} />,
                  title: "Hydroponically Grown",
                  text: "Modern farming techniques for consistent quality.",
                },
                {
                  icon: <ShieldCheck size={25} />,
                  title: "Quality Controlled",
                  text: "Controlled growing conditions throughout the process.",
                },
                {
                  icon: <Truck size={25} />,
                  title: "Reliable Supply",
                  text: "Suitable for homes, businesses and bulk requirements.",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="rounded-2xl bg-white p-6 shadow-sm"
                >

                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
                    {item.icon}
                  </div>

                  <h3 className="mt-5 font-bold">
                    {item.title}
                  </h3>

                  <p className="mt-2 text-sm leading-6 text-gray-500">
                    {item.text}
                  </p>

                </div>
              ))}

            </div>

          </div>

        </section>


        {/* ================================================
            RELATED PRODUCTS
        ================================================= */}

        <section className="py-14">

          <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

            <div className="flex items-end justify-between">

              <div>

                <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                  Explore More
                </p>

                <h2 className="mt-2 text-3xl font-extrabold">
                  Related Products
                </h2>

              </div>

              <Link
                to="/products"
                className="hidden items-center gap-2 text-sm font-bold text-[#087f4f] sm:flex"
              >
                View All Products
                <ArrowRight size={16} />
              </Link>

            </div>


            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">

              {relatedProducts.map((item) => (
                <Link
                  key={item.slug}
                  to={`/products/${item.slug}`}
                  className="group overflow-hidden rounded-2xl border border-gray-200 bg-white transition hover:-translate-y-1 hover:shadow-lg"
                >

                  <div className="overflow-hidden">

                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-48 w-full object-cover transition duration-500 group-hover:scale-105"
                    />

                  </div>

                  <div className="p-5">

                    <p className="text-xs font-bold uppercase text-[#16854f]">
                      {item.category}
                    </p>

                    <h3 className="mt-1 font-bold">
                      {item.name}
                    </h3>

                    <p className="mt-2 text-sm text-gray-500">
                      {item.description}
                    </p>

                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#087f4f]">
                      View Details
                      <ArrowRight size={15} />
                    </span>

                  </div>

                </Link>
              ))}

            </div>

          </div>

        </section>


        {/* ================================================
            CTA
        ================================================= */}

        <section className="bg-[#005536] py-12">

          <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-6 px-5 text-center md:flex-row md:px-10 md:text-left">

            <div>

              <p className="text-sm font-bold uppercase tracking-[0.15em] text-green-300">
                Fresh • Local • Sustainable
              </p>

              <h2 className="mt-2 text-2xl font-extrabold text-white sm:text-3xl">
                Looking for Fresh {product.name}?
              </h2>

              <p className="mt-2 text-sm text-white/70">
                Contact us for availability, bulk orders and
                business enquiries.
              </p>

            </div>

            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-[#087f4f]"
            >
              Contact Us
              <ArrowRight size={16} />
            </Link>

          </div>

        </section>

      </main>

    </>
  );
}